// "They booked on the link" (owner request, PJ, 2026-10-20: "sometimes when we're on the calls, we like to book meetings
// by doing it through the Calendly links and not through the dialer ... there's an option to mark it if you booked it
// through the Calendly link or to book it through the dialer itself"): the Book panel's list of Calendly bookings the
// dialer hasn't linked, as pure functions. Unit-tested in tests/fd-book-calendly-link.test.ts. The server decides what is
// listed, what is "Likely theirs" and what may be linked (src/worker/calendly/link.ts).
import type { QueueItem } from '../../../src/shared/api';
import type { CalendlyLinkRequest, CalendlyUnlinkedBooking } from '../../../src/shared/api-core';
import { fmtDayTime } from './time';

/** While the list is open it asks again this often, for this long (a booking reaches the dialer a few seconds after
 *  it is made); Refresh asks now and starts the two minutes again. */
export const LINK_POLL_MS = 5_000;
export const LINK_POLL_FOR_MS = 2 * 60_000;

/** The Book panel's three ways, in order (PJ 2026-10-20). */
export type BookMode = 'calendly' | 'link' | 'manual';
export const BOOK_MODES: ReadonlyArray<{ mode: BookMode; label: string }> = [
  { mode: 'calendly', label: 'Book a time' },
  { mode: 'link', label: 'They booked on the link' },
  { mode: 'manual', label: 'Booked elsewhere' },
];

/** Why "Book a time" is off on the lead on screen, or null: Calendly slots belong to a campaign lead. */
export function bookTimeProblem(item: Pick<QueueItem, 'noCampaign'>): string | null {
  return item.noCampaign
    ? 'Book a time needs a campaign lead. On a call with no campaign, pick the booking they made on the link, or log one booked elsewhere.'
    : null;
}

/** The mode the panel opens in: a no-campaign pad call opens on the link list (it can't book a time). */
export function initialBookMode(item: Pick<QueueItem, 'noCampaign'>): BookMode {
  return item.noCampaign ? 'link' : 'calendly';
}

/** "booked just now", "booked 3 min ago", "booked 2 h ago", "booked 3 days ago". */
export function bookedAgo(receivedAt: string, now: number): string {
  const t = Date.parse(receivedAt);
  if (!Number.isFinite(t)) return 'booked at an unknown time';
  const min = Math.max(0, Math.floor((now - t) / 60_000));
  if (min < 1) return 'booked just now';
  if (min < 60) return `booked ${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `booked ${h} h ago`;
  return `booked ${Math.floor(h / 24)} days ago`;
}

/** The meeting time in the lead's zone ("Thu, Oct 22, 2:00 PM CDT"), or what is known. */
export function meetingWhen(b: Pick<CalendlyUnlinkedBooking, 'startTime'>, tz: string): string {
  if (!b.startTime) return 'time not known yet';
  try { return fmtDayTime(new Date(b.startTime), tz); } catch { return b.startTime; }
}

/** Who booked and what they called the business: "Pat Owner · “Sunset Dispensary”". */
export function bookingWho(b: Pick<CalendlyUnlinkedBooking, 'inviteeName' | 'inviteeEmail' | 'businessAnswer'>): string {
  const who = b.inviteeName ?? b.inviteeEmail ?? 'Someone';
  return b.businessAnswer ? `${who} · “${b.businessAnswer}”` : who;
}

/** The lead on screen as the list's hints and the link's target: the campaign lead, or the business on a pad call. */
export function linkTarget(item: Pick<QueueItem, 'noCampaign' | 'membership' | 'business'>, callId: number | null): { callId: number | null; membershipId: number | null; businessId: number | null } {
  return item.noCampaign
    ? { callId, membershipId: null, businessId: item.business.id }
    : { callId, membershipId: item.membership.id, businessId: null };
}

/** POST /api/meetings/link-calendly for the booking picked and the lead on screen. */
export function linkRequest(inviteeUri: string, target: ReturnType<typeof linkTarget>): CalendlyLinkRequest {
  return {
    inviteeUri,
    ...(target.callId ? { callId: target.callId } : {}),
    ...(target.membershipId ? { membershipId: target.membershipId } : {}),
    ...(target.businessId ? { businessId: target.businessId } : {}),
  };
}

/** A picked booking that dropped off the list (linked by someone else, or canceled) is unpicked. */
export function keptPick(picked: string | null, bookings: ReadonlyArray<Pick<CalendlyUnlinkedBooking, 'inviteeUri'>>): string | null {
  return picked && bookings.some((b) => b.inviteeUri === picked) ? picked : null;
}
